import { EndpointBuilder } from '@reduxjs/toolkit/dist/query/endpointDefinitions';
import { BaseQueryFn, FetchArgs, FetchBaseQueryError } from '@reduxjs/toolkit/query';
import type {
  Event,
  CreateEventRequest,
  UpdateEventRequest,
  CheckPasswordRequest,
  CheckPasswordResponse,
  EventDetailResponse,
  EventActionResponse,
  ZipRequestResponse,
  ZipStatusResponse
} from '../../types/event';
import type { Photo } from '../../types/photos';
import type { ApiResponse } from '../../types/api';

// EventApi endpoints
export const eventApiEndpoints = (builder: EndpointBuilder<BaseQueryFn<string | FetchArgs, unknown, FetchBaseQueryError>, never, 'api'>) => ({
  getEvents: builder.query<Event[], void>({
    query: () => '/events',
    transformResponse: (response: ApiResponse<Event[]>) => response.data,
    providesTags: (result) =>
      result
        ? [
            ...result.map(({ id }) => ({ type: 'Events' as const, id })),
            { type: 'Events', id: 'LIST' },
          ]
        : [{ type: 'Events', id: 'LIST' }],
  }),
  getEventDetails: builder.query<Event, string | number>({
    query: (id) => `/events/detail/${id}`,
    transformResponse: (response: EventDetailResponse) => response.data,
    providesTags: (_result, _error, id) => [{ type: 'EventDetails', id }],
  }),
  createEvent: builder.mutation<Event, CreateEventRequest>({
    query: (data) => ({
      url: '/events',
      method: 'POST',
      body: data,
    }),
    transformResponse: (response: ApiResponse<Event>) => response.data,
    invalidatesTags: [{ type: 'Events', id: 'LIST' }],
  }),
  updateEvent: builder.mutation<Event, { url: string; data: UpdateEventRequest }>({
    query: ({ url, data }) => ({
      url: `/events/${url}`,
      method: 'PUT',
      body: data,
    }),
    transformResponse: (response: ApiResponse<Event>) => response.data,
    invalidatesTags: (_result, _error, { url }) => [
      { type: 'Events', id: 'LIST' },
      { type: 'EventDetails', id: url },
    ],
  }),
  deleteEvent: builder.mutation<boolean, string>({
    query: (url) => ({
      url: `/events/${url}`,
      method: 'DELETE',
    }),
    transformResponse: (response: EventActionResponse) => response.success,
    invalidatesTags: [{ type: 'Events', id: 'LIST' }],
  }),
  checkEventPassword: builder.mutation<{
    success: boolean;
    event?: Event;
    photos?: Photo[];
    eventTitle?: string;
  }, {
    url: string;
    password: string;
  }>({
    query: ({ url, password }) => ({
      url: `/events/url/${url}/check-password`,
      method: 'POST',
      body: { password },
      credentials: 'omit'
    }),
    invalidatesTags: (result) => 
      result?.success ? [{ type: 'Photos', id: 'PUBLIC' }] : []
  }),
  generateQRCode: builder.query<Blob, string>({
    query: (eventUrl) => ({
      url: `/events/${eventUrl}/qrcode`,
      params: { size: 512 },
      responseHandler: async (response) => response.blob()
    }),
    transformResponse: (response: Blob) => response,
    providesTags: (_result, _error, eventUrl) => [{ type: 'QRCode', id: eventUrl }]
  }),
  getEventByUrl: builder.query<{
    success: boolean;
    message: string;
    data: {
      event: Event;
      photos: Photo[];
    };
  }, string>({
    query: (url) => `/events/${url}`,
    providesTags: (_result, _error, url) => [{ type: 'Events', id: url }],
  }),
}); 